import React, { useState, useEffect, useRef } from 'react';
import dayjs from "dayjs";

const HOUR_MINUTE_FORMAT_REGEX = new RegExp('((h|H){1,2}):((m|M){1,2})');
const SECONDS_FORMAT_REGEX = new RegExp('((h|H){1,2}):((m|M){1,2}):((s|S){1,2})');

const loadLocale = async () => {
  let locale;
  switch (document.body.dataset.locale) {
    case 'fi':
      locale = (await import('flatpickr/dist/l10n/fi.js')).Finnish;
      break;
    case 'sv':
      locale = (await import('flatpickr/dist/l10n/sv.js')).Swedish;
      break;
    case 'fr':
      locale = (await import('flatpickr/dist/l10n/fr.js')).French;
      break;
    default:
      locale = 'default';
  }
  return locale;
}

const useFlatpickr = (inputRef, onChange, setInputValue, options = {}, overlay = false) => {
  const currentFormat = options.format || 'YYYY-MM-DD';
  
  const initialized = useRef(false);

  useEffect(() => {
    let datePicker;
    let observer;

    const initFlatPickr = async (el) => {
      if (!initialized.current) {
        const activeLocale = await loadLocale();
        datePicker = el.flatpickr({
          locale: activeLocale,
          ...(options.monthYearPicker ? {
              plugins: [
                monthSelectPlugin({shorthand: true, dateFormat: "Y-m", altFormat: "F Y"})
              ]
            } : {
              ...options,
              enableTime: currentFormat.match(HOUR_MINUTE_FORMAT_REGEX) ? true : false,
              enableSeconds: options.altInput ? (options.altFormat || 'YYYY-MM-DD').match(SECONDS_FORMAT_REGEX) : (currentFormat.match(SECONDS_FORMAT_REGEX) ? true : false),
              dateFormat: options.dateFormat || 'Y-m-d',
              parseDate: (dateStr, format) => {
                const parsed = dayjs(dateStr, format === 'Y-m-d' ? currentFormat : format, false);
                return parsed.isValid() ? parsed.toDate() : undefined;
              },
              formatDate: (date, format) => {
                return dayjs(date).format(format === 'Y-m-d' ? currentFormat : format);
              },
            }
          ),
          onValueUpdate: (selectedDates) => {
            // console.log('selectedDates',selectedDates)
            setInputValue(selectedDates.length ? dayjs(selectedDates[0]).format(currentFormat) : '');
          },
          onChange: (selectedDates, dateStr, instance) => {
            // Using date object to be safe on native inputs
            const formattedDate = selectedDates.length ? dayjs(selectedDates[0]).format(currentFormat) : '';
            onChange(selectedDates, formattedDate, instance);
          }
        })
        initialized.current = true;
      }
    };

    const el = inputRef.current;
    if (el) {
      el.focus({preventScroll: true});
      initFlatPickr(el);
    } else {
      // Waiting for modals - Observer is used only when element is not found
      observer = new MutationObserver(() => {
        const elCheck = inputRef.current;
        if (elCheck) {
          elCheck.focus({preventScroll: true});
          initFlatPickr(elCheck);
          observer.disconnect(); // stop once initialized
        }
      });

      observer.observe(document.documentElement, {childList: true, subtree: true});
    }

    // Remove event listener on unmount
    return () => {
      if (datePicker && initialized.current) {
        datePicker.destroy();
        initialized.current = false;
      }

      if (observer) observer.disconnect();
    };
  }, []);
}

export default useFlatpickr;
